import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

// Import seat data
import federalSeatsData from '../data/federal-seats.json';

// ============ CONSTANTS ============
const COLORS = {
  background: '#F5F0E6',
  burgundy: '#800020',
  gold: '#D4AF37',
  cream: '#FDF8F0',
  black: '#1a1a1a',
  parties: {
    ALP: '#C8102E',
    LNP: '#0047AB',
    GRN: '#10C25B',
    ONP: '#FF6600',
    NATS: '#FFD700',
    OTH: '#808080',
    IND: '#9370DB'
  }
};

const PARTY_NAMES = {
  ALP: 'Labor',
  LNP: 'Liberal',
  GRN: 'Greens',
  ONP: 'One Nation',
  NATS: 'Nationals',
  OTH: 'Others'
};

const DEFAULT_VOTES = {
  ALP: 32,
  LNP: 36,
  GRN: 12,
  ONP: 10,
  OTH: 10
};

const BASELINE_2PP = 50; // 2025 baseline Labor 2PP

// ============ CALCULATION ENGINE ============
class ElectionCalculator {
  constructor(seatsData, preferenceFlows) {
    this.seats = seatsData.seats;
    this.metadata = seatsData.metadata;
    this.preferenceFlows = preferenceFlows || seatsData.preference_flows;
  }

  // Calculate 2PP from primary votes
  calculate2PP(primaryVotes, customFlows = null) {
    const flows = customFlows || this.preferenceFlows;
    
    // Labor 2PP = ALP% + (GRN% × flow) + (ONP% × flow) + (OTH% × flow)
    const labor2PP = 
      primaryVotes.ALP +
      (primaryVotes.GRN * (flows.Greens?.to_Labor || 0.882)) +
      (primaryVotes.ONP * (flows.One_Nation?.to_Labor || 0.255)) +
      (primaryVotes.OTH * (flows.Other?.to_Labor || 0.45));
    
    const coalition2PP = 100 - labor2PP;
    
    return { labor: labor2PP, coalition: coalition2PP };
  }

  // Calculate swing from baseline
  calculateSwing(current2PP) {
    return current2PP.labor - BASELINE_2PP;
  }

  // Project individual seat based on primary votes
  projectSeat(seat, swing, primaryVotes) {
    const baseline = seat.baseline_2025;
    const sensitivity = seat.swing_sensitivity || 1.0;
    const competitors = seat.competing_parties || ['ALP', 'LNP'];
    
    // EXTREME SCENARIO HANDLING
    // If any party has overwhelming majority (>80%), they win all contestable seats
    const dominantParty = Object.entries(primaryVotes).find(([k, v]) => v >= 80);
    if (dominantParty) {
      const [party, vote] = dominantParty;
      const partyMap = { ALP: 'Labor', LNP: 'Coalition', GRN: 'Greens', ONP: 'One Nation', OTH: 'Other' };
      const winner = partyMap[party] || party;
      const margin = (vote - 50) * sensitivity;
      
      return {
        name: seat.name,
        state: seat.state,
        holder: baseline.holder,
        margin: margin,
        winner: winner,
        swing: swing * sensitivity,
        safety: this.getSafety(margin),
        isFlip: winner !== baseline.holder,
        competitors: competitors
      };
    }
    
    // Handle three-way contests
    if (seat.is_three_way) {
      return this.projectThreeWay(seat, swing, primaryVotes);
    }
    
    // For ALP vs GRN contests
    if (competitors.includes('GRN') && !competitors.includes('LNP')) {
      return this.projectALPvsGRN(seat, swing, primaryVotes);
    }
    
    // For LNP vs OTH (Independent) contests
    if (competitors.includes('OTH') && !competitors.includes('ALP')) {
      return this.projectLNPvsOTH(seat, swing, primaryVotes);
    }
    
    // For LNP vs ONP contests (without ALP)
    if (competitors.includes('ONP') && !competitors.includes('ALP')) {
      return this.projectLNPvsONP(seat, swing, primaryVotes);
    }
    
    // For ONP vs OTH contests (without ALP or LNP)
    if (!competitors.includes('ALP') && !competitors.includes('LNP')) {
      return this.projectMinorPartyContest(seat, swing, primaryVotes);
    }
    
    // Standard ALP vs LNP two-party contest
    // Calculate effective swing based on actual vote changes
    const alpChange = primaryVotes.ALP - 32; // baseline ALP primary
    const lnpChange = primaryVotes.LNP - 36; // baseline LNP primary
    const effectiveSwing = (alpChange - lnpChange) * 0.5;
    
    let newMargin;
    if (baseline.holder === 'Labor') {
      // Labor holds if margin + swing > 0
      newMargin = baseline.margin + (effectiveSwing * sensitivity);
    } else if (baseline.holder === 'Coalition') {
      // Coalition holds if margin - swing > 0  
      newMargin = baseline.margin - (effectiveSwing * sensitivity);
    } else {
      // Other holders (independents, etc)
      newMargin = baseline.margin - (effectiveSwing * sensitivity * 0.5);
    }
    
    // Determine winner
    let winner;
    if (newMargin > 0) {
      winner = baseline.holder;
    } else {
      // Seat flips
      if (baseline.holder === 'Labor') {
        winner = 'Coalition';
      } else if (baseline.holder === 'Coalition') {
        winner = 'Labor';
      } else {
        // Other holder loses to highest primary
        winner = primaryVotes.ALP > primaryVotes.LNP ? 'Labor' : 'Coalition';
      }
    }
    
    return {
      name: seat.name,
      state: seat.state,
      holder: baseline.holder,
      margin: Math.abs(newMargin),
      winner: winner,
      swing: effectiveSwing * sensitivity,
      safety: this.getSafety(Math.abs(newMargin)),
      isFlip: winner !== baseline.holder,
      competitors: competitors
    };
  }

  // ALP vs Greens contest (inner city seats)
  projectALPvsGRN(seat, swing, primaryVotes) {
    const baseline = seat.baseline_2025;
    const sensitivity = seat.swing_sensitivity || 1.0;
    
    // Use scaling for extreme scenarios
    const alpScale = primaryVotes.ALP / 32;
    const grnScale = primaryVotes.GRN / 12;
    
    // Estimate local primaries based on national scaling
    const basePrimary = baseline.primary_votes || { ALP: 40, GRN: 30 };
    const localALP = basePrimary.ALP * alpScale;
    const localGRN = basePrimary.GRN * grnScale;
    
    // In ALP vs GRN, winner is determined by scaled primaries
    // Add preferences from others (LNP prefs split roughly 50/50 in inner city)
    const lnpVotes = (basePrimary.LNP || 20) * (primaryVotes.LNP / 36);
    const alpFinal = localALP + lnpVotes * 0.4;
    const grnFinal = localGRN + lnpVotes * 0.6;
    
    const margin = Math.abs(alpFinal - grnFinal);
    const winner = alpFinal > grnFinal ? 'Labor' : 'Greens';
    
    return {
      name: seat.name,
      state: seat.state,
      holder: baseline.holder,
      margin: margin,
      winner: winner,
      swing: swing * sensitivity,
      safety: this.getSafety(margin),
      isFlip: winner !== baseline.holder,
      competitors: ['ALP', 'GRN']
    };
  }

  // LNP vs Independent contest (teal seats)
  projectLNPvsOTH(seat, swing, primaryVotes) {
    const baseline = seat.baseline_2025;
    const sensitivity = seat.swing_sensitivity || 1.0;
    
    // Use scaling for extreme scenarios
    const lnpScale = primaryVotes.LNP / 36;
    const othScale = primaryVotes.OTH / 10;
    const alpScale = primaryVotes.ALP / 32;
    
    // Estimate local primaries
    const basePrimary = baseline.primary_votes || { LNP: 35, OTH: 30, ALP: 20 };
    const localLNP = basePrimary.LNP * lnpScale;
    const localOTH = basePrimary.OTH * othScale;
    const localALP = basePrimary.ALP * alpScale;
    
    // ALP preferences flow to independents in teal seats (70-80%)
    const lnpFinal = localLNP + localALP * 0.25;
    const othFinal = localOTH + localALP * 0.75;
    
    const margin = Math.abs(lnpFinal - othFinal);
    const winner = lnpFinal > othFinal ? 'Coalition' : 'Other';
    
    return {
      name: seat.name,
      state: seat.state,
      holder: baseline.holder,
      margin: margin,
      winner: winner,
      swing: swing * sensitivity,
      safety: this.getSafety(margin),
      isFlip: winner !== baseline.holder,
      competitors: ['LNP', 'OTH']
    };
  }

  // LNP vs One Nation contest (Queensland outback)
  projectLNPvsONP(seat, swing, primaryVotes) {
    const baseline = seat.baseline_2025;
    const sensitivity = seat.swing_sensitivity || 1.0;
    
    const lnpScale = primaryVotes.LNP / 36;
    const onpScale = primaryVotes.ONP / 10;
    const alpScale = primaryVotes.ALP / 32;
    
    const basePrimary = baseline.primary_votes || { LNP: 35, ONP: 30, ALP: 20 };
    const localLNP = Math.max(0, basePrimary.LNP * lnpScale);
    const localONP = Math.max(0, basePrimary.ONP * onpScale);
    const localALP = Math.max(0, basePrimary.ALP * alpScale);
    
    // ALP preferences flow mostly to LNP over ONP in these contests
    const lnpFinal = localLNP + localALP * 0.6;
    const onpFinal = localONP + localALP * 0.4;
    
    const margin = Math.abs(lnpFinal - onpFinal);
    const winner = lnpFinal > onpFinal ? 'Coalition' : 'One Nation';
    
    return {
      name: seat.name,
      state: seat.state,
      holder: baseline.holder,
      margin: margin,
      winner: winner,
      swing: swing * sensitivity,
      safety: this.getSafety(margin),
      isFlip: winner !== baseline.holder,
      competitors: ['LNP', 'ONP']
    };
  }

  // Minor party vs minor party contest (ONP vs OTH)
  projectMinorPartyContest(seat, swing, primaryVotes) {
    const baseline = seat.baseline_2025;
    const sensitivity = seat.swing_sensitivity || 1.0;
    const competitors = seat.competing_parties || ['ONP', 'OTH'];
    
    const onpScale = primaryVotes.ONP / 10;
    const othScale = primaryVotes.OTH / 10;
    const alpScale = primaryVotes.ALP / 32;
    const lnpScale = primaryVotes.LNP / 36;
    
    const basePrimary = baseline.primary_votes || { ONP: 30, OTH: 30, ALP: 15, LNP: 15 };
    const localONP = Math.max(0, (basePrimary.ONP || 0) * onpScale);
    const localOTH = Math.max(0, (basePrimary.OTH || 0) * othScale);
    const localALP = Math.max(0, (basePrimary.ALP || 0) * alpScale);
    const localLNP = Math.max(0, (basePrimary.LNP || 0) * lnpScale);
    
    // Distribute major party preferences
    // In ONP vs OTH, ALP prefs go mostly to OTH, LNP prefs go mostly to ONP
    const onpFinal = localONP + localLNP * 0.7 + localALP * 0.2;
    const othFinal = localOTH + localLNP * 0.3 + localALP * 0.8;
    
    const margin = Math.abs(onpFinal - othFinal);
    const winner = onpFinal > othFinal ? 'One Nation' : 'Other';
    
    return {
      name: seat.name,
      state: seat.state,
      holder: baseline.holder,
      margin: margin,
      winner: winner,
      swing: swing * sensitivity,
      safety: this.getSafety(margin),
      isFlip: winner !== baseline.holder,
      competitors: competitors
    };
  }

  // Handle three-way contests (ONP competitive)
  projectThreeWay(seat, swing, primaryVotes) {
    const baseline = seat.baseline_2025;
    const basePrimary = baseline.primary_votes;
    const sensitivity = seat.swing_sensitivity || 1.0;
    
    // Calculate national-to-local scaling factor for each party
    // This makes extreme national swings have bigger local effects
    const alpScale = primaryVotes.ALP / 32; // Relative to baseline
    const lnpScale = primaryVotes.LNP / 36;
    const onpScale = primaryVotes.ONP / 10;
    const grnScale = primaryVotes.GRN / 12;
    const othScale = primaryVotes.OTH / 10;
    
    // Adjust primaries - scale the local primary by the national change
    const adjustedPrimary = {
      ALP: Math.max(0, basePrimary.ALP * alpScale),
      LNP: Math.max(0, basePrimary.LNP * lnpScale),
      ONP: Math.max(0, basePrimary.ONP * onpScale),
      GRN: Math.max(0, basePrimary.GRN * grnScale),
      OTH: Math.max(0, basePrimary.OTH * othScale)
    };
    
    // Find the top 2 parties in this seat
    const competitors = seat.competing_parties || ['ALP', 'LNP', 'ONP'];
    const competitorVotes = competitors.map(p => ({ party: p, votes: adjustedPrimary[p] || 0 }));
    competitorVotes.sort((a, b) => b.votes - a.votes);
    
    // Top 2 go to final count
    const top2 = competitorVotes.slice(0, 2);
    const eliminated = competitorVotes.slice(2);
    
    // Distribute preferences from eliminated candidates
    let party1Votes = top2[0].votes;
    let party2Votes = top2[1].votes;
    
    for (const elim of eliminated) {
      const prefToLabor = elim.party === 'ONP' ? 0.255 : 
                          elim.party === 'GRN' ? 0.882 : 
                          elim.party === 'LNP' ? 0.2 : 0.45;
      
      if (top2[0].party === 'ALP') {
        party1Votes += elim.votes * prefToLabor;
        party2Votes += elim.votes * (1 - prefToLabor);
      } else if (top2[1].party === 'ALP') {
        party2Votes += elim.votes * prefToLabor;
        party1Votes += elim.votes * (1 - prefToLabor);
      } else {
        // Neither is ALP - distribute based on which is more "right"
        if (top2[0].party === 'LNP') {
          party1Votes += elim.votes * (1 - prefToLabor);
          party2Votes += elim.votes * prefToLabor * 0.5;
        } else {
          party2Votes += elim.votes * (1 - prefToLabor);
          party1Votes += elim.votes * prefToLabor * 0.5;
        }
      }
    }
    
    const winnerParty = party1Votes > party2Votes ? top2[0].party : top2[1].party;
    const margin = Math.abs(party1Votes - party2Votes) / 2;
    
    // Map party codes to display names
    const winner = winnerParty === 'LNP' ? 'Coalition' : 
                   winnerParty === 'ALP' ? 'Labor' : 
                   winnerParty === 'ONP' ? 'One Nation' :
                   winnerParty === 'GRN' ? 'Greens' : 'Other';
    
    return {
      name: seat.name,
      state: seat.state,
      holder: baseline.holder,
      margin: margin,
      winner: winner,
      swing: swing,
      safety: this.getSafety(margin),
      isFlip: winner !== baseline.holder,
      isThreeWay: true,
      competitors: [top2[0].party, top2[1].party]
    };
  }

  getSafety(margin) {
    if (margin >= 10) return 'SAFE';
    if (margin >= 6) return 'FIRM';
    if (margin >= 3) return 'LEAN';
    return 'TOSS-UP';
  }

  // Project all seats
  projectAllSeats(primaryVotes, customFlows = null) {
    const tpp = this.calculate2PP(primaryVotes, customFlows);
    const swing = this.calculateSwing(tpp);
    
    const results = {
      Labor: 0,
      Coalition: 0,
      Greens: 0,
      'One Nation': 0,
      Independents: 0,
      Others: 0,
      seats: []
    };
    
    for (const seat of this.seats) {
      const projection = this.projectSeat(seat, swing, primaryVotes);
      results.seats.push(projection);
      
      // Count winners
      if (projection.winner === 'Labor') results.Labor++;
      else if (projection.winner === 'Coalition') results.Coalition++;
      else if (projection.winner === 'Greens') results.Greens++;
      else if (projection.winner === 'One Nation') results['One Nation']++;
      else if (projection.winner === 'Other') results.Others++;
      else results.Independents++;
    }
    
    return {
      ...results,
      tpp,
      swing,
      majority: 76,
      government: results.Labor >= 76 ? 'Labor Majority' : 
                  results.Coalition >= 76 ? 'Coalition Majority' : 'Hung Parliament'
    };
  }

  // Senate STV calculation
  calculateSenate(primaryVotes, customFlows = null) {
    const flows = customFlows || this.preferenceFlows;
    const states = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS'];
    const seatsPerState = 6; // Half-Senate election
    const quota = 14.29; // 100 / 7
    
    const results = {
      Labor: 0,
      Coalition: 0,
      Greens: 0,
      'One Nation': 0,
      Others: 0,
      byState: {}
    };
    
    for (const state of states) {
      // Adjust votes slightly by state
      const stateAdjustment = this.getStateAdjustment(state);
      const stateVotes = {
        ALP: primaryVotes.ALP * stateAdjustment.ALP,
        LNP: primaryVotes.LNP * stateAdjustment.LNP,
        GRN: primaryVotes.GRN * stateAdjustment.GRN,
        ONP: primaryVotes.ONP * stateAdjustment.ONP,
        OTH: primaryVotes.OTH * stateAdjustment.OTH
      };
      
      // Normalize
      const total = Object.values(stateVotes).reduce((a, b) => a + b, 0);
      Object.keys(stateVotes).forEach(k => stateVotes[k] = (stateVotes[k] / total) * 100);
      
      // Calculate quotas
      const quotas = {
        ALP: stateVotes.ALP / quota,
        LNP: stateVotes.LNP / quota,
        GRN: stateVotes.GRN / quota,
        ONP: stateVotes.ONP / quota,
        OTH: stateVotes.OTH / quota
      };
      
      // Allocate seats by STV
      const stateSeats = this.allocateSTVSeats(quotas, seatsPerState, flows);
      results.byState[state] = stateSeats;
      
      results.Labor += stateSeats.ALP || 0;
      results.Coalition += stateSeats.LNP || 0;
      results.Greens += stateSeats.GRN || 0;
      results['One Nation'] += stateSeats.ONP || 0;
      results.Others += stateSeats.OTH || 0;
    }
    
    // Add territory senators (2 each for ACT and NT)
    results.Labor += 2; // ACT
    results.Labor += 1; // NT
    results.Coalition += 1; // NT
    
    // Add continuing senators (not up for election)
    results.Labor += 10;
    results.Coalition += 14;
    results.Greens += 4;
    results['One Nation'] += 2;
    results.Others += 2;
    
    const totalSeats = results.Labor + results.Coalition + results.Greens + 
                       results['One Nation'] + results.Others;
    
    return {
      ...results,
      total: totalSeats,
      majority: 39,
      control: results.Labor + results.Greens >= 39 ? 'Labor + Greens' :
               results.Coalition >= 39 ? 'Coalition' : 'Crossbench Balance of Power'
    };
  }

  getStateAdjustment(state) {
    const adjustments = {
      'NSW': { ALP: 1.0, LNP: 1.0, GRN: 1.0, ONP: 0.9, OTH: 1.0 },
      'VIC': { ALP: 1.05, LNP: 0.95, GRN: 1.1, ONP: 0.8, OTH: 1.0 },
      'QLD': { ALP: 0.95, LNP: 1.0, GRN: 0.9, ONP: 1.3, OTH: 1.0 },
      'SA': { ALP: 1.0, LNP: 1.0, GRN: 1.05, ONP: 1.0, OTH: 1.0 },
      'WA': { ALP: 1.02, LNP: 0.96, GRN: 1.1, ONP: 1.15, OTH: 1.05 },
      'TAS': { ALP: 1.0, LNP: 0.95, GRN: 1.15, ONP: 0.85, OTH: 1.1 }
    };
    return adjustments[state] || { ALP: 1, LNP: 1, GRN: 1, ONP: 1, OTH: 1 };
  }

  allocateSTVSeats(quotas, totalSeats, flows) {
    const seats = { ALP: 0, LNP: 0, GRN: 0, ONP: 0, OTH: 0 };
    let remaining = totalSeats;
    const currentQuotas = { ...quotas };
    
    // First pass: allocate full quotas
    for (const party of Object.keys(currentQuotas)) {
      const fullQuotas = Math.floor(currentQuotas[party]);
      seats[party] = fullQuotas;
      remaining -= fullQuotas;
      currentQuotas[party] -= fullQuotas;
    }
    
    // Second pass: allocate remaining seats by highest remainder
    while (remaining > 0) {
      let highestParty = null;
      let highestRemainder = 0;
      
      for (const party of Object.keys(currentQuotas)) {
        if (currentQuotas[party] > highestRemainder) {
          highestRemainder = currentQuotas[party];
          highestParty = party;
        }
      }
      
      if (highestParty) {
        seats[highestParty]++;
        currentQuotas[highestParty] = 0;
        remaining--;
      } else {
        break;
      }
    }
    
    return seats;
  }
}

// ============ COMPONENTS ============

// Art Deco Frame Component
function ArtDecoFrame({ children, title }) {
  return (
    <div className="relative p-1" style={{ background: `linear-gradient(135deg, ${COLORS.gold} 0%, ${COLORS.burgundy} 50%, ${COLORS.gold} 100%)` }}>
      <div className="p-4" style={{ backgroundColor: COLORS.background }}>
        {title && (
          <div className="text-center mb-4">
            <div className="inline-block px-6 py-2 relative">
              <div className="absolute inset-0" style={{ 
                background: COLORS.burgundy,
                clipPath: 'polygon(5% 0, 95% 0, 100% 50%, 95% 100%, 5% 100%, 0 50%)'
              }}></div>
              <h2 className="relative text-lg font-bold text-white tracking-widest" style={{ fontFamily: 'Playfair Display, serif' }}>
                {title}
              </h2>
            </div>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

// Semicircular Parliament Diagram - FIXED to show seat count above dots
function ParliamentDiagram({ seats, total, title }) {
  const rows = 5;
  const seatsPerRow = Math.ceil(total / rows);
  
  // Convert seats object to array of party assignments
  const seatAssignments = [];
  Object.entries(seats).forEach(([party, count]) => {
    for (let i = 0; i < count; i++) {
      seatAssignments.push(party);
    }
  });
  
  return (
    <div className="relative">
      {/* Sunburst background */}
      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-full h-32 overflow-hidden">
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2" style={{
          width: '300px',
          height: '150px',
          background: `radial-gradient(ellipse at bottom, ${COLORS.gold}40 0%, transparent 70%)`
        }}></div>
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute bottom-0 left-1/2 origin-bottom"
            style={{
              width: '2px',
              height: '120px',
              background: `linear-gradient(to top, ${COLORS.gold}60, transparent)`,
              transform: `translateX(-50%) rotate(${-90 + (i * 15)}deg)`
            }}
          ></div>
        ))}
      </div>
      
      {/* Parliament arc */}
      <svg viewBox="0 0 200 120" className="w-full max-w-xs mx-auto relative z-10">
        {/* Title with background for visibility */}
        <rect x="50" y="2" width="100" height="18" fill={COLORS.background} />
        <text x="100" y="15" textAnchor="middle" fill={COLORS.burgundy} fontSize="12" fontWeight="bold" style={{ fontFamily: 'Playfair Display, serif' }}>
          {title}
        </text>
        
        {[...Array(rows)].map((_, row) => {
          const radius = 45 + row * 12;
          const seatsInRow = Math.min(seatsPerRow - row * 2, total - row * seatsPerRow);
          const startIdx = row * seatsPerRow;
          
          return [...Array(Math.max(0, seatsInRow + row * 2))].map((_, seatIdx) => {
            const totalInRow = seatsInRow + row * 2;
            const angle = Math.PI - (seatIdx / (totalInRow - 1 || 1)) * Math.PI;
            const x = 100 + Math.cos(angle) * radius;
            const y = 105 - Math.sin(angle) * radius;
            
            const globalIdx = startIdx + seatIdx;
            const party = seatAssignments[globalIdx] || 'OTH';
            const color = party === 'Labor' ? COLORS.parties.ALP :
                         party === 'Coalition' ? COLORS.parties.LNP :
                         party === 'Greens' ? COLORS.parties.GRN :
                         party === 'One Nation' ? COLORS.parties.ONP :
                         COLORS.parties.OTH;
            
            return (
              <circle
                key={`${row}-${seatIdx}`}
                cx={x}
                cy={y}
                r={3}
                fill={color}
                stroke={COLORS.black}
                strokeWidth={0.3}
              />
            );
          });
        })}
      </svg>
    </div>
  );
}

// Primary Vote Slider - FIXED to allow independent adjustment
function VoteSlider({ party, value, onChange, color, disabled }) {
  const [inputValue, setInputValue] = useState(value.toFixed(1));
  
  useEffect(() => {
    setInputValue(value.toFixed(1));
  }, [value]);
  
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };
  
  const handleInputBlur = () => {
    const numValue = parseFloat(inputValue);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      onChange(party, numValue);
    } else {
      setInputValue(value.toFixed(1));
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };
  
  return (
    <div className="flex items-center gap-3 mb-3">
      <div className="w-24 flex items-center gap-2">
        <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: color }}></div>
        <span className="text-sm font-semibold" style={{ color: COLORS.black }}>{PARTY_NAMES[party] || party}</span>
      </div>
      <div className="flex-1">
        <Slider
          value={[value]}
          onValueChange={(v) => onChange(party, v[0])}
          max={100}
          min={0}
          step={0.5}
          disabled={disabled}
          className="cursor-pointer"
        />
      </div>
      <div className="w-20">
        <Input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          className="h-8 text-sm text-right font-bold px-2"
          style={{ color: COLORS.burgundy }}
        />
      </div>
    </div>
  );
}

// Electorate Table Row
function ElectorateRow({ seat, index }) {
  const bgColor = index % 2 === 0 ? COLORS.cream : COLORS.background;
  const winnerColor = seat.winner === 'Labor' ? COLORS.parties.ALP :
                      seat.winner === 'Coalition' ? COLORS.parties.LNP :
                      seat.winner === 'Greens' ? COLORS.parties.GRN :
                      seat.winner === 'One Nation' ? COLORS.parties.ONP :
                      COLORS.parties.OTH;
  
  return (
    <tr style={{ backgroundColor: bgColor }} className={seat.isFlip ? 'ring-2 ring-yellow-400' : ''}>
      <td className="px-2 py-1 text-sm font-medium">{seat.name}</td>
      <td className="px-2 py-1 text-sm text-center">{seat.state}</td>
      <td className="px-2 py-1 text-sm text-center">{seat.holder}</td>
      <td className="px-2 py-1 text-sm text-center">
        <span className="px-2 py-0.5 rounded text-white text-xs font-bold" style={{ backgroundColor: winnerColor }}>
          {seat.winner}
        </span>
      </td>
      <td className="px-2 py-1 text-sm text-center">{seat.margin.toFixed(1)}%</td>
      <td className="px-2 py-1 text-sm text-center">
        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
          seat.safety === 'SAFE' ? 'bg-green-600 text-white' :
          seat.safety === 'FIRM' ? 'bg-blue-500 text-white' :
          seat.safety === 'LEAN' ? 'bg-yellow-500 text-black' :
          'bg-red-500 text-white'
        }`}>
          {seat.safety}
        </span>
      </td>
      <td className="px-2 py-1 text-sm text-center">
        {seat.isFlip && (
          <span className="text-yellow-600 font-bold">FLIP</span>
        )}
      </td>
    </tr>
  );
}

// Preference Flow Modal
function PreferenceFlowModal({ isOpen, onClose, flows, onSave }) {
  const [localFlows, setLocalFlows] = useState(flows);
  
  useEffect(() => {
    setLocalFlows(flows);
  }, [flows]);
  
  const updateFlow = (party, target, value) => {
    const newFlows = { ...localFlows };
    newFlows[party] = { ...newFlows[party], [target]: parseFloat(value) };
    
    // Auto-calculate the other flow
    if (target === 'to_Labor') {
      newFlows[party].to_Coalition = 1 - parseFloat(value);
    } else {
      newFlows[party].to_Labor = 1 - parseFloat(value);
    }
    
    setLocalFlows(newFlows);
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" style={{ backgroundColor: COLORS.background }}>
        <DialogHeader>
          <DialogTitle style={{ color: COLORS.burgundy, fontFamily: 'Playfair Display, serif' }}>
            Adjust Preference Flows
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {Object.entries(localFlows).map(([party, flow]) => (
            <div key={party} className="p-3 rounded" style={{ backgroundColor: COLORS.cream }}>
              <h4 className="font-bold mb-2" style={{ color: COLORS.burgundy }}>{party}</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <label className="block text-xs text-gray-600">To Labor</label>
                  <Input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={flow.to_Labor?.toFixed(3) || 0}
                    onChange={(e) => updateFlow(party, 'to_Labor', e.target.value)}
                    className="h-8"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600">To Coalition</label>
                  <Input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={flow.to_Coalition?.toFixed(3) || 0}
                    onChange={(e) => updateFlow(party, 'to_Coalition', e.target.value)}
                    className="h-8"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button 
            onClick={() => { onSave(localFlows); onClose(); }}
            className="flex-1"
            style={{ backgroundColor: COLORS.burgundy, color: 'white' }}
          >
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Share Preview Modal - NEW
function SharePreviewModal({ isOpen, onClose, imageDataUrl, onDownload, onCopy }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" style={{ backgroundColor: COLORS.background }}>
        <DialogHeader>
          <DialogTitle style={{ color: COLORS.burgundy, fontFamily: 'Playfair Display, serif' }}>
            Share Your Results
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4">
          {imageDataUrl && (
            <img src={imageDataUrl} alt="Election Results" className="w-full rounded border" />
          )}
        </div>
        
        <div className="flex gap-3 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button 
            onClick={onCopy}
            className="flex-1"
            style={{ backgroundColor: COLORS.gold, color: COLORS.black }}
          >
            Copy to Clipboard
          </Button>
          <Button 
            onClick={onDownload}
            className="flex-1"
            style={{ backgroundColor: COLORS.burgundy, color: 'white' }}
          >
            Download Image
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ MAIN COMPONENT ============
export default function ElectionSimulator() {
  const [primaryVotes, setPrimaryVotes] = useState(DEFAULT_VOTES);
  const [preferenceFlows, setPreferenceFlows] = useState(federalSeatsData.preference_flows);
  const [showPreferenceModal, setShowPreferenceModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareImageUrl, setShareImageUrl] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('margin');
  const [generating, setGenerating] = useState(false);
  
  // Initialize calculator
  const calculator = useMemo(() => new ElectionCalculator(federalSeatsData, preferenceFlows), [preferenceFlows]);
  
  // Calculate projections
  const houseResults = useMemo(() => {
    return calculator.projectAllSeats(primaryVotes, preferenceFlows);
  }, [primaryVotes, preferenceFlows, calculator]);
  
  const senateResults = useMemo(() => {
    return calculator.calculateSenate(primaryVotes, preferenceFlows);
  }, [primaryVotes, preferenceFlows, calculator]);
  
  // Handle vote change - NO AUTO-NORMALIZATION
  const handleVoteChange = useCallback((party, value) => {
    setPrimaryVotes(prev => ({
      ...prev,
      [party]: value
    }));
  }, []);
  
  // Total validation
  const voteTotal = Object.values(primaryVotes).reduce((a, b) => a + b, 0);
  const isValid = Math.abs(voteTotal - 100) < 0.5;
  const isOver = voteTotal > 100.5;
  const isUnder = voteTotal < 99.5;
  
  // Filter and sort seats
  const filteredSeats = useMemo(() => {
    let seats = [...houseResults.seats];
    
    if (searchTerm) {
      seats = seats.filter(s => 
        s.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (stateFilter !== 'ALL') {
      seats = seats.filter(s => s.state === stateFilter);
    }
    
    seats.sort((a, b) => {
      if (sortBy === 'margin') return a.margin - b.margin;
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'state') return a.state.localeCompare(b.state);
      return 0;
    });
    
    return seats;
  }, [houseResults.seats, searchTerm, stateFilter, sortBy]);
  
  // Generate share image - returns data URL
  const generateShareImage = async () => {
    setGenerating(true);
    try {
      // Create canvas
      const canvas = document.createElement('canvas');
      canvas.width = 1200;
      canvas.height = 800;
      const ctx = canvas.getContext('2d');
      
      // Background
      ctx.fillStyle = COLORS.background;
      ctx.fillRect(0, 0, 1200, 800);
      
      // Border
      ctx.strokeStyle = COLORS.gold;
      ctx.lineWidth = 8;
      ctx.strokeRect(20, 20, 1160, 760);
      ctx.strokeStyle = COLORS.burgundy;
      ctx.lineWidth = 4;
      ctx.strokeRect(30, 30, 1140, 740);
      
      // Title
      ctx.fillStyle = COLORS.burgundy;
      ctx.font = 'bold 48px Georgia, serif';
      ctx.textAlign = 'center';
      ctx.fillText('ELECTION SIMULATOR RESULTS', 600, 80);
      
      ctx.font = '24px Georgia, serif';
      ctx.fillText('FEDERAL ELECTION PROJECTION', 600, 115);
      
      // House of Reps results
      ctx.font = 'bold 28px Georgia, serif';
      ctx.fillText('HOUSE OF REPRESENTATIVES', 300, 180);
      
      const houseSeats = [
        { party: 'LABOR', count: houseResults.Labor, color: COLORS.parties.ALP },
        { party: 'COALITION', count: houseResults.Coalition, color: COLORS.parties.LNP },
        { party: 'GREENS', count: houseResults.Greens, color: COLORS.parties.GRN },
        { party: 'ONE NATION', count: houseResults['One Nation'], color: COLORS.parties.ONP },
        { party: 'OTHERS', count: houseResults.Others + houseResults.Independents, color: COLORS.parties.OTH }
      ];
      
      let y = 220;
      ctx.font = '20px sans-serif';
      ctx.textAlign = 'left';
      houseSeats.forEach(s => {
        ctx.fillStyle = s.color;
        ctx.fillRect(100, y - 15, 20, 20);
        ctx.fillStyle = COLORS.black;
        ctx.fillText(`${s.party}: ${s.count}`, 130, y);
        y += 30;
      });
      
      // Government status
      ctx.font = 'bold 24px sans-serif';
      ctx.fillStyle = COLORS.burgundy;
      ctx.fillText(houseResults.government, 100, y + 20);
      
      // Senate results
      ctx.textAlign = 'center';
      ctx.font = 'bold 28px Georgia, serif';
      ctx.fillText('SENATE', 900, 180);
      
      const senateSeats = [
        { party: 'LABOR', count: senateResults.Labor, color: COLORS.parties.ALP },
        { party: 'COALITION', count: senateResults.Coalition, color: COLORS.parties.LNP },
        { party: 'GREENS', count: senateResults.Greens, color: COLORS.parties.GRN },
        { party: 'ONE NATION', count: senateResults['One Nation'], color: COLORS.parties.ONP },
        { party: 'OTHERS', count: senateResults.Others, color: COLORS.parties.OTH }
      ];
      
      y = 220;
      ctx.textAlign = 'left';
      senateSeats.forEach(s => {
        ctx.fillStyle = s.color;
        ctx.fillRect(700, y - 15, 20, 20);
        ctx.fillStyle = COLORS.black;
        ctx.fillText(`${s.party}: ${s.count}`, 730, y);
        y += 30;
      });
      
      ctx.font = 'bold 24px sans-serif';
      ctx.fillStyle = COLORS.burgundy;
      ctx.fillText(senateResults.control, 700, y + 20);
      
      // Primary votes
      ctx.textAlign = 'center';
      ctx.font = 'bold 24px Georgia, serif';
      ctx.fillStyle = COLORS.burgundy;
      ctx.fillText('PRIMARY VOTE', 600, 520);
      
      ctx.font = '18px sans-serif';
      ctx.fillStyle = COLORS.black;
      const voteText = Object.entries(primaryVotes)
        .map(([k, v]) => `${PARTY_NAMES[k] || k}: ${v.toFixed(1)}%`)
        .join('  |  ');
      ctx.fillText(voteText, 600, 550);
      
      // 2PP
      ctx.font = 'bold 20px sans-serif';
      ctx.fillText(`Two-Party Preferred: ALP ${houseResults.tpp.labor.toFixed(1)}% - ${houseResults.tpp.coalition.toFixed(1)}% LNP`, 600, 590);
      
      // Branding
      ctx.font = 'bold 16px sans-serif';
      ctx.fillStyle = COLORS.burgundy;
      ctx.fillText('Overton Solutions Election Simulator', 600, 760);
      
      // Get data URL
      const dataUrl = canvas.toDataURL('image/png');
      setShareImageUrl(dataUrl);
      setShowShareModal(true);
      
    } catch (error) {
      console.error(error);
      toast.error('Failed to generate image');
    }
    setGenerating(false);
  };
  
  const handleDownload = () => {
    if (shareImageUrl) {
      const link = document.createElement('a');
      link.download = 'election-projection.png';
      link.href = shareImageUrl;
      link.click();
      toast.success('Image downloaded!');
    }
  };
  
  const handleCopyToClipboard = async () => {
    if (shareImageUrl) {
      try {
        const response = await fetch(shareImageUrl);
        const blob = await response.blob();
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ]);
        toast.success('Image copied to clipboard!');
      } catch (error) {
        console.error(error);
        toast.error('Failed to copy image. Try downloading instead.');
      }
    }
  };
  
  return (
    <div className="min-h-screen p-4" style={{ backgroundColor: COLORS.background }}>
      {/* Main Container with Art Deco Border */}
      <div className="max-w-7xl mx-auto">
        <div className="p-2" style={{ background: `linear-gradient(135deg, ${COLORS.gold} 0%, ${COLORS.burgundy} 50%, ${COLORS.gold} 100%)` }}>
          <div className="p-6" style={{ backgroundColor: COLORS.background }}>
            
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-5xl font-bold tracking-wider mb-2" style={{ 
                color: COLORS.burgundy, 
                fontFamily: 'Playfair Display, serif',
                textShadow: `2px 2px 0 ${COLORS.gold}`
              }}>
                ELECTION SIMULATOR
              </h1>
              <p className="text-lg tracking-widest" style={{ color: COLORS.black }}>
                FEDERAL ELECTION PROJECTION
              </p>
              <div className="flex justify-center gap-2 mt-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-8 h-1" style={{ backgroundColor: i % 2 === 0 ? COLORS.gold : COLORS.burgundy }}></div>
                ))}
              </div>
            </div>
            
            {/* Main Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Left Column - Primary Votes */}
              <div>
                <ArtDecoFrame title="PRIMARY VOTE">
                  <div className="space-y-1">
                    {Object.entries(primaryVotes).map(([party, value]) => (
                      <VoteSlider
                        key={party}
                        party={party}
                        value={value}
                        onChange={handleVoteChange}
                        color={COLORS.parties[party]}
                        disabled={false}
                      />
                    ))}
                  </div>
                  
                  {/* Total indicator - shows warning if not 100% */}
                  <div className={`mt-4 p-2 rounded text-center text-sm font-bold ${
                    isValid ? 'bg-green-100 text-green-800' : 
                    isOver ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    Total: {voteTotal.toFixed(1)}% 
                    {isValid && ' ✓'}
                    {isOver && ` (${(voteTotal - 100).toFixed(1)}% over - reduce votes)`}
                    {isUnder && ` (${(100 - voteTotal).toFixed(1)}% under - add votes)`}
                  </div>
                  
                  {/* 2PP Display */}
                  <div className="mt-4 p-3 rounded" style={{ backgroundColor: COLORS.cream }}>
                    <div className="text-xs font-bold mb-2" style={{ color: COLORS.burgundy }}>
                      TWO-PARTY PREFERRED
                    </div>
                    <div className="flex justify-between items-center">
                      <span style={{ color: COLORS.parties.ALP }} className="font-bold">
                        ALP {houseResults.tpp.labor.toFixed(1)}%
                      </span>
                      <div className="flex-1 mx-2 h-3 rounded overflow-hidden bg-gray-200">
                        <div 
                          className="h-full transition-all duration-300"
                          style={{ 
                            width: `${Math.min(100, Math.max(0, houseResults.tpp.labor))}%`,
                            backgroundColor: COLORS.parties.ALP
                          }}
                        ></div>
                      </div>
                      <span style={{ color: COLORS.parties.LNP }} className="font-bold">
                        {houseResults.tpp.coalition.toFixed(1)}% LNP
                      </span>
                    </div>
                  </div>
                  
                  {/* Preference Flow Button */}
                  <Button
                    onClick={() => setShowPreferenceModal(true)}
                    variant="outline"
                    className="w-full mt-4"
                    style={{ borderColor: COLORS.burgundy, color: COLORS.burgundy }}
                  >
                    Adjust Preference Flows
                  </Button>
                </ArtDecoFrame>
              </div>
              
              {/* Center Column - House of Reps */}
              <div>
                <ArtDecoFrame title="HOUSE OF REPS">
                  <ParliamentDiagram
                    seats={{
                      Labor: houseResults.Labor,
                      Coalition: houseResults.Coalition,
                      Greens: houseResults.Greens,
                      'One Nation': houseResults['One Nation'],
                      Others: houseResults.Others + houseResults.Independents
                    }}
                    total={150}
                    title="150 SEATS"
                  />
                  
                  {/* Seat counts */}
                  <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4" style={{ backgroundColor: COLORS.parties.ALP }}></div>
                      <span>Labor: <strong>{houseResults.Labor}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4" style={{ backgroundColor: COLORS.parties.LNP }}></div>
                      <span>Coalition: <strong>{houseResults.Coalition}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4" style={{ backgroundColor: COLORS.parties.GRN }}></div>
                      <span>Greens: <strong>{houseResults.Greens}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4" style={{ backgroundColor: COLORS.parties.ONP }}></div>
                      <span>One Nation: <strong>{houseResults['One Nation']}</strong></span>
                    </div>
                    <div className="flex items-center gap-2 col-span-2">
                      <div className="w-4 h-4" style={{ backgroundColor: COLORS.parties.OTH }}></div>
                      <span>Others/Ind: <strong>{houseResults.Others + houseResults.Independents}</strong></span>
                    </div>
                  </div>
                  
                  {/* Government status */}
                  <div className="mt-4 p-3 text-center rounded font-bold" style={{
                    backgroundColor: houseResults.Labor >= 76 ? COLORS.parties.ALP :
                                     houseResults.Coalition >= 76 ? COLORS.parties.LNP : 
                                     '#FFD700',
                    color: 'white'
                  }}>
                    {houseResults.government}
                  </div>
                </ArtDecoFrame>
              </div>
              
              {/* Right Column - Senate */}
              <div>
                <ArtDecoFrame title="SENATE">
                  <ParliamentDiagram
                    seats={{
                      Labor: senateResults.Labor,
                      Coalition: senateResults.Coalition,
                      Greens: senateResults.Greens,
                      'One Nation': senateResults['One Nation'],
                      Others: senateResults.Others
                    }}
                    total={76}
                    title="76 SEATS"
                  />
                  
                  {/* Seat counts */}
                  <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4" style={{ backgroundColor: COLORS.parties.ALP }}></div>
                      <span>Labor: <strong>{senateResults.Labor}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4" style={{ backgroundColor: COLORS.parties.LNP }}></div>
                      <span>Coalition: <strong>{senateResults.Coalition}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4" style={{ backgroundColor: COLORS.parties.GRN }}></div>
                      <span>Greens: <strong>{senateResults.Greens}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4" style={{ backgroundColor: COLORS.parties.ONP }}></div>
                      <span>One Nation: <strong>{senateResults['One Nation']}</strong></span>
                    </div>
                    <div className="flex items-center gap-2 col-span-2">
                      <div className="w-4 h-4" style={{ backgroundColor: COLORS.parties.OTH }}></div>
                      <span>Others: <strong>{senateResults.Others}</strong></span>
                    </div>
                  </div>
                  
                  {/* Control status */}
                  <div className="mt-4 p-3 text-center rounded font-bold text-white" style={{
                    backgroundColor: senateResults.control.includes('Labor') ? COLORS.parties.ALP :
                                     senateResults.control.includes('Coalition') ? COLORS.parties.LNP : 
                                     COLORS.parties.OTH
                  }}>
                    {senateResults.control}
                  </div>
                </ArtDecoFrame>
              </div>
            </div>
            
            {/* Share Button */}
            <div className="flex justify-center mt-8">
              <Button
                onClick={generateShareImage}
                disabled={generating}
                className="px-8 py-3 text-lg"
                style={{ backgroundColor: COLORS.burgundy, color: COLORS.cream }}
              >
                {generating ? 'GENERATING...' : 'SHARE YOUR RESULTS NOW'}
              </Button>
            </div>
            
            {/* Electorate Table */}
            <div className="mt-8">
              <ArtDecoFrame title="ELECTORATE PREDICTION">
                {/* Filters */}
                <div className="flex flex-wrap gap-4 mb-4">
                  <Input
                    placeholder="Search electorate..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-48"
                  />
                  <select
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    className="px-3 py-2 border rounded"
                    style={{ borderColor: COLORS.burgundy }}
                  >
                    <option value="ALL">All States</option>
                    <option value="NSW">NSW</option>
                    <option value="VIC">VIC</option>
                    <option value="QLD">QLD</option>
                    <option value="WA">WA</option>
                    <option value="SA">SA</option>
                    <option value="TAS">TAS</option>
                    <option value="ACT">ACT</option>
                    <option value="NT">NT</option>
                  </select>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2 border rounded"
                    style={{ borderColor: COLORS.burgundy }}
                  >
                    <option value="margin">Sort by Margin</option>
                    <option value="name">Sort by Name</option>
                    <option value="state">Sort by State</option>
                  </select>
                </div>
                
                {/* Table */}
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full border-collapse">
                    <thead className="sticky top-0" style={{ backgroundColor: COLORS.burgundy }}>
                      <tr>
                        <th className="px-2 py-2 text-left text-white text-sm">Electorate</th>
                        <th className="px-2 py-2 text-center text-white text-sm">State</th>
                        <th className="px-2 py-2 text-center text-white text-sm">Holder</th>
                        <th className="px-2 py-2 text-center text-white text-sm">Projected</th>
                        <th className="px-2 py-2 text-center text-white text-sm">Margin</th>
                        <th className="px-2 py-2 text-center text-white text-sm">Safety</th>
                        <th className="px-2 py-2 text-center text-white text-sm">Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSeats.map((seat, idx) => (
                        <ElectorateRow key={seat.name} seat={seat} index={idx} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </ArtDecoFrame>
            </div>
            
            {/* Footer */}
            <div className="text-center mt-8 text-sm" style={{ color: COLORS.black }}>
              <p>This is a simulation tool for educational purposes. Actual election results may vary.</p>
              <p className="font-bold mt-2" style={{ color: COLORS.burgundy }}>Overton Solutions</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modals */}
      <PreferenceFlowModal
        isOpen={showPreferenceModal}
        onClose={() => setShowPreferenceModal(false)}
        flows={preferenceFlows}
        onSave={setPreferenceFlows}
      />
      
      <SharePreviewModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        imageDataUrl={shareImageUrl}
        onDownload={handleDownload}
        onCopy={handleCopyToClipboard}
      />
    </div>
  );
}
